import { Apollo } from 'apollo-angular';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { ModalHostDirective } from '@app/core/directives/modal-host.directive';
import { ModalService } from '@app/core/services/modal/modal.service';
import { environment } from '@environments/environment';
import { captureException, setUser } from '@sentry/angular';
import {
  User,
  Company,
  OnAcceptLineItemsGQL,
  NewOrderGQL,
  OnWontPackLineItemsGQL,
  OnRevertLineItemsToCreatedGQL,
  OnTemplateOutputStateChangeGQL,
  OnAddendumHeaderStateChangeGQL,
  OnReportCompleteGQL,
  OnRefreshReportGQL,
  OnPalletOutOutputStateChangeGQL,
  OnPalletInOutputStateChangeGQL,
  OnShipmentStateChangeGQL,
  OnAccountStatementStateChangeGQL,
  UserQuery,
  AccountStatement,
  OnArrivalQcGenerationStateChangeGQL,
  OnAccountingExportStateChangeGQL,
  CompanyStateEnum,
  OnNotificationEventGQL,
  ChannelNotification,
  OnNotificationEventSubscription,
  NotificationRuleSeverityEnum,
  OnWhatsappInteractionFailedToDeliverGQL,
  OnWhatsappInteractionFailedToDeliverSubscription,
  WhatsappInteractionStatus,
} from '@app/generated/graphql';
import { UserHelper } from '@app/core/helpers/user.helper';
import { UrlTrackerService } from '@app/core/services/url-tracker/url-tracker.service';
import { Router, RouterOutlet } from '@angular/router';

import { EventBusService } from '@app/core/services/event-bus/event-bus.service';
import { catchError, filter, map, mergeMap, tap } from 'rxjs/operators';
import { handleFlowError } from '@app/shared/operators/handle-flow-error.operator';
import { EMPTY } from 'rxjs';
import { StatementDownloadService } from './payments/statement-download.service';
import { ArrivalQcGenerationService } from './quality-and-compliance/arrival-qc-generation.service';
import { UserService } from '@app/core/services/user.service';
import { TranslatorService } from '@app/core/services/translation/translator.service';
import { AccountingExportService } from '@app/shared/invoice-modules/accounting-export.service';
import { NavService } from '@app/core/services/nav.service';
import { ConfirmationService, MessageService } from '@openng/optimus-ui/api';
import { DiscardConfirmationService } from '@app/shared/discard-confirmation-modal/discard-confirmation.service';
import { PermissionService } from '@app/core/services/permission.service';
import { NotificationsDataService } from './notifications/services/notifications-data.service';
import { NotificationService } from '@app/core/services/notification/notification.service';
import { INotificationMessage } from './notifications-and-alerts/interface';
import { NotificationsOrchestratorService } from '@app/shared/global-layout/components/notifications-v2/notifications-orchestrator.service';
import { AlertsOrchestratorService } from '@app/shared/global-layout/components/alerts-menu/alerts-orchestrator.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GlobalLayoutComponent } from '../shared/global-layout/global-layout.component';
import { Bind } from '@openng/optimus-ui/bind';
import { ConfirmDialog } from '@openng/optimus-ui/confirmdialog';
import { Toast } from '@openng/optimus-ui/toast';
import { NotificationsAndAlertsComponent } from './notifications-and-alerts/notifications-and-alerts.component';
import { DiscardConfirmationModalComponent } from '../shared/discard-confirmation-modal/discard-confirmation-modal.component';
import { ModalHostDirective as ModalHostDirective_1 } from '../core/directives/modal-host.directive';
import { ExecutionResult } from 'graphql';
import { AnalyticsService } from '@app/core/services/mixpanel/analytics.service';

@Component({
  selector: 'ag-account',
  templateUrl: './account.component.html',
  providers: [TranslatorService, ConfirmationService],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    GlobalLayoutComponent,
    RouterOutlet,
    Bind,
    ConfirmDialog,
    Toast,
    NotificationsAndAlertsComponent,
    DiscardConfirmationModalComponent,
    ModalHostDirective_1,
  ],
})
export class AccountComponent implements OnInit {
  private modalService = inject(ModalService);
  private urlTrackerService = inject(UrlTrackerService);
  private router = inject(Router);
  private newOrderGql = inject(NewOrderGQL);
  private apollo = inject(Apollo);
  private onAcceptLineItemsGql = inject(OnAcceptLineItemsGQL);
  private onWontPackLineItemsGql = inject(OnWontPackLineItemsGQL);
  private onRevertLineItemsToCreatedGql = inject(OnRevertLineItemsToCreatedGQL);
  private _analytics = inject(AnalyticsService);
  private onTemplateChangeGql = inject(OnTemplateOutputStateChangeGQL);
  private onAddendumHeaderStateChangeGql = inject(
    OnAddendumHeaderStateChangeGQL,
  );
  private onPalletOutOutputStateChangeGql = inject(
    OnPalletOutOutputStateChangeGQL,
  );
  private onPalletInOutputStateChangeGql = inject(
    OnPalletInOutputStateChangeGQL,
  );
  private onNotificationEventGQL = inject(OnNotificationEventGQL);
  private onReportCompleteGQL = inject(OnReportCompleteGQL);
  private onRefreshReportGQL = inject(OnRefreshReportGQL);
  private onShipmentStateChangeGQL = inject(OnShipmentStateChangeGQL);
  private eventBus = inject(EventBusService);
  private userService = inject(UserService);
  private translatorService = inject(TranslatorService);
  private onAccountStatementStateChangeGQL = inject(
    OnAccountStatementStateChangeGQL,
  );
  private statementDownloadService = inject(StatementDownloadService);
  private onArrivalQcGenerationStateChangeGQL = inject(
    OnArrivalQcGenerationStateChangeGQL,
  );
  private arrivalQcGenerationService = inject(ArrivalQcGenerationService);
  private onAccountingExportStateChangeGQL = inject(
    OnAccountingExportStateChangeGQL,
  );
  private accountingExportService = inject(AccountingExportService);
  private navService = inject(NavService);
  private permissionService = inject(PermissionService);
  private notificationsDataService = inject(NotificationsDataService);
  private _notificationService = inject(NotificationService);
  private _notificationsOrchestratorService = inject(
    NotificationsOrchestratorService,
  );
  private _alertsOrchestratorService = inject(AlertsOrchestratorService);
  private _onWhatsappInteractionFailedToDeliverGQL = inject(
    OnWhatsappInteractionFailedToDeliverGQL,
  );
  private _messageService = inject(MessageService);

  // subscription cleanup
  private _destroyRef = inject(DestroyRef);

  // constants
  private readonly SEASON_SETUP_REDIRECT_KEY = 'season-setup-redirect' as const;

  @ViewChild(ModalHostDirective, { static: true })
  modalHost: ModalHostDirective;

  myCompany: Company;
  user: User;
  initialized = false;

  isExistingUser: boolean = null;
  isAgrigateUser: boolean = null;
  hasRedirectedToSeasonSetup: boolean = false;
  discardService: DiscardConfirmationService;

  constructor() {
    const discardConfirmationService = inject(DiscardConfirmationService);

    this.discardService = discardConfirmationService;
    // Ensures that permissions are initialized before any components within account module are initialized.
    // This guarantees that we have the user credentials and don't need to await for them in each component.
    this.permissionService.init();
    this._getSeasonSetupRedirectFromLocalStorage();
  }

  async ngOnInit(): Promise<void> {
    this.modalService.setHost(this.modalHost);

    this.userService
      .fetchUserQueryData$()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((userQuery: UserQuery) => {
        if (!userQuery) {
          this.handleUserQueryError();
          return;
        }

        if (!this.initialized) {
          this.initialized = true;

          this.mixpanelTrackingSetup();

          // when dealing with share tokens the user ID will be null because
          // there is no user associated with a share token, only a company.
          let userId = 'unknown';
          if (this.user.id) {
            userId = this.user.id.toString();
          }

          setUser({
            id: userId,
            email: this.user.email,
          });

          this.urlTrackerService.trackPreviousUrl();
        }

        this.translatorService.init();
      });

    this.userService.company$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(Boolean),
        tap((company: Company) => {
          this.myCompany = company;
        }),
        tap((company: Company) => this._handleSeasonSetupRedirect(company)),
      )
      .subscribe();

    this.userService.user$
      .pipe(takeUntilDestroyed(this._destroyRef), filter(Boolean))
      .subscribe((user: User) => {
        this.user = user;
        this.isExistingUser = UserHelper.isExistingUser(this.user);
        this.isAgrigateUser = UserHelper.isAgrigateUser(this.user);
      });

    this.newOrderGql
      .subscribe()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((_) => {
        this.apollo.client.clearStore();
      });

    this.onAcceptLineItemsGql
      .subscribe()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        this.apollo.client.clearStore();
      });

    this.onWontPackLineItemsGql
      .subscribe()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        this.apollo.client.clearStore();
      });

    this.onRevertLineItemsToCreatedGql
      .subscribe()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        this.apollo.client.clearStore();
      });

    this.initTemplateSubscription();
    this.initPPECBAddendumStateSubscription();
    this.initReportSubscription();
    this.initRefreshReportSubscription();
    this.initPalletOutSubscription();
    this.initPalletInSubscription();
    this.initShipmentStateSubscription();
    this.initAccountStatementSubscription();
    this.initWhatsappInteractionStatusChangeSubscription();
    this.initArrivalQcGenerationSubscription();
    this.initAccountingExportSubscription();
    this.initNotificationSubscription();
  }

  private handleUserQueryError(): void {
    this.router.navigate(['account', '500'], { replaceUrl: true });
  }

  mixpanelTrackingSetup() {
    // don't track events for agrigate users in production
    if (environment.production && this.isAgrigateUser) return;

    this._analytics.init();
    this._analytics.identify(this.user.externalIdentifier, {
      $first_name: this.user.firstName,
      $last_name: this.user.lastName,
      $email: this.user.email,
      $company_id: this.user.company.id,
      $role_name: this.user.role,
      $company_name: this.user.company.name,
    });
  }

  initTemplateSubscription() {
    // templates are generated async on the backend, so we use a subscription to
    // dynamically update the link to a template document. A simple event bus
    // is used to tell interested components that they should fetch the data again.

    this.onTemplateChangeGql
      .subscribe()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        const file = res?.data?.onTemplateOutputStateChange;

        if (!file) {
          return;
        }

        const type = file?.templateDataSourceType;
        const id = file?.templateDataSourceId;
        switch (type) {
          case 'AccountStatement':
          case 'Invoice':
          case 'LoadOutInstruction':
          case 'AddendumHeader':
            this.createNamespacedChannel(id, type);
        }
      });
  }

  initPPECBAddendumStateSubscription() {
    // addendums are generated async on the backend, so we use a subscription to
    // dynamically update the state of an addendum. A simple event bus
    // is used to tell interested components that they should fetch the data again.
    this.onAddendumHeaderStateChangeGql
      .subscribe()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        const addendumHeader = res?.data?.onAddendumHeaderStateChange;
        const id = addendumHeader?.id;

        this.createNamespacedChannel(id, 'AddendumHeader');
      });
  }

  /**
   * An export notification is being done for the entire shipment,
   * multiple api calls have to be made many different records. We fetch
   * the shipment after it has completed with it's updated state and data.
   */
  initShipmentStateSubscription(): void {
    this.onShipmentStateChangeGQL
      .subscribe()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        const shipment = res?.data?.onShipmentStateChange;
        const id = shipment?.id;

        this.createNamespacedChannel(id, 'Shipment');
      });
  }

  initPalletOutSubscription() {
    // pallet outs are generated async on the backend, so we use a subscription to
    // dynamically update the state of a pallet out. A simple event bus
    // is used to tell interested components that they should refetch the data.
    // in this case we want to refetch the addendum header, since the pallet out
    // is not a top level entity.
    this.onPalletOutOutputStateChangeGql
      .subscribe()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        const palletOut = res?.data?.onPalletOutOutputStateChange;
        const parentId = palletOut?.addendumHeaderId;

        this.createNamespacedChannel(parentId, 'AddendumHeader');
      });
  }

  initPalletInSubscription() {
    // pallet ins are generated async on the backend, so we use a subscription to
    // dynamically update the state of a pallet in. A simple event bus
    // is used to tell interested components that they should refetch the data.
    // in this case we want to refetch the loi, since the pallet in
    // is not a top level entity.
    this.onPalletInOutputStateChangeGql
      .subscribe()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        const palletIn = res?.data?.onPalletInOutputStateChange;
        const parentId = palletIn?.loadOutInstructionId;

        this.createNamespacedChannel(parentId, 'LoadOutInstruction');
      });
  }

  initReportSubscription() {
    this.onReportCompleteGQL
      .subscribe()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        const requestUuid = res?.data?.onReportComplete?.requestUuid;
        const status = res?.data?.onReportComplete?.status;

        this.createNamespacedStateChannel(requestUuid, 'Report', status);
      });
  }

  initRefreshReportSubscription() {
    this.onRefreshReportGQL
      .subscribe()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        const id = res?.data?.onRefreshReport.id;
        this.createNamespacedChannel(id.toString(), 'ActualReport');
      });
  }

  initNotificationSubscription(): void {
    this.onNotificationEventGQL
      .subscribe()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        mergeMap((result: ExecutionResult<OnNotificationEventSubscription>) => {
          // Get the notification ID from the subscription result.
          // This is used to fetch the full channel notification from the backend.
          const notificationId: string | number | undefined =
            result?.data?.newInAppNotification?.id;

          if (!notificationId) {
            console.warn('No notification ID received from subscription');
            return EMPTY;
          }

          return this.notificationsDataService
            .getNotification$(notificationId)
            .pipe(
              catchError((err) => {
                captureException(err);
                return EMPTY;
              }),
            );
        }),
        tap((notification: ChannelNotification) => {
          // Add the notification to the appropriate orchestrator service.
          const severity: NotificationRuleSeverityEnum | undefined =
            notification?.notificationRule?.severity;

          if (!severity) return;

          switch (severity) {
            case NotificationRuleSeverityEnum.Info:
              this._notificationsOrchestratorService.addIncomingNotification(
                notification,
              );
              break;
            case NotificationRuleSeverityEnum.Alert:
              this._alertsOrchestratorService.addIncomingAlert(notification);
              break;
          }
        }),
        // Transform the channel notification to a notification message so that it can be displayed in the headless toast component.
        map((notification: ChannelNotification): INotificationMessage[] =>
          notification
            ? this._notificationService.transformChannelNotificationsToNotificationMessages(
                [notification],
              )
            : [],
        ),
        tap((formattedNotifications: INotificationMessage[]) =>
          // Show the notifications in the headless toast component.
          this._notificationService.showNotifications(formattedNotifications),
        ),
      )
      .subscribe();
  }

  initWhatsappInteractionStatusChangeSubscription() {
    this._onWhatsappInteractionFailedToDeliverGQL
      .subscribe()
      .pipe(
        tap(
          (
            res: ExecutionResult<OnWhatsappInteractionFailedToDeliverSubscription>,
          ) => {
            const interaction: WhatsappInteractionStatus | null =
              res.data?.onWhatsappInteractionFailedToDeliver;
            if (!interaction) {
              return;
            }

            this._messageService.add({
              severity: 'error',
              summary: 'Whatsapp message failed to send',
              detail: `Offer recipient ${interaction.buyerName} failed to receive the message.`,
            });
          },
        ),
        handleFlowError(
          this._messageService,
          'WhatsApp delivery monitoring stopped — refresh to resume',
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  initAccountStatementSubscription() {
    // start watching for account statement changes

    this.statementDownloadService.setupStatementDownload();

    this.onAccountStatementStateChangeGQL
      .subscribe()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        const accountStatement: AccountStatement =
          res?.data?.onAccountStatementStateChange;
        const id: string = accountStatement?.id;

        this.createGlobalChannel(id, 'AccountStatement');
      });
  }

  initArrivalQcGenerationSubscription(): void {
    this.onArrivalQcGenerationStateChangeGQL
      .subscribe()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        this.arrivalQcGenerationService.updateArrivalQcGenerationData(
          res?.data?.onArrivalQcGenerationStateChange,
        );
      });
  }

  initAccountingExportSubscription(): void {
    this.onAccountingExportStateChangeGQL
      .subscribe()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        const accountingExport = res.data?.onAccountingExportStateChange;
        const id: string = accountingExport?.id;

        this.createGlobalChannel(id, 'AccountingExport');
      });
  }

  // state is stored per page/ feature/ url etc. with a status variable
  createNamespacedStateChannel(id: string, type: string, status: string) {
    this.eventBus.setChannel(`Refetch${type}`, { id, status });
  }

  // state is stored per page/ feature/ url etc.
  createNamespacedChannel(id: string, type: string) {
    this.eventBus.setChannel(`Refetch${type}`, id);
  }

  // state is stored globally across all pages/ features/ urls etc.
  createGlobalChannel(id: string, type: string) {
    this.eventBus.setChannel(`Refetch${type}`, id, false);
  }

  /**
   * Redirects to the season setup page if the company is in the onboarding state.
   * The redirect is stored in local storage to prevent multiple redirects.
   */
  private _redirectToSeasonSetup(): void {
    if (this.hasRedirectedToSeasonSetup) {
      return;
    }

    this._setSeasonSetupRedirectToLocalStorage();
    this.router.navigate(['account', 'season-setup']);
  }

  /**
   * Gets the season setup redirect from local storage. This is used to prevent
   * redirecting to the season setup if the user has already been redirected once
   * before while the company is in the onboarding state.
   */
  private _getSeasonSetupRedirectFromLocalStorage(): void {
    const redirect = localStorage.getItem(this.SEASON_SETUP_REDIRECT_KEY);

    if (redirect) {
      this.hasRedirectedToSeasonSetup = true;
    } else {
      this.hasRedirectedToSeasonSetup = false;
    }
  }

  /**
   * Stores the season setup redirect in local storage. This is used to prevent
   * redirecting to the season setup if the user has already been redirected once
   * before while the company is in the onboarding state.
   */
  private _setSeasonSetupRedirectToLocalStorage(): void {
    localStorage.setItem(this.SEASON_SETUP_REDIRECT_KEY, 'true');
    this.hasRedirectedToSeasonSetup = true;
  }

  /**
   * Handles the season setup redirect. This is used to show the season setup
   * navigation and redirect to the season setup page if the company is in the
   * onboarding state.
   */
  private _handleSeasonSetupRedirect(company: Company): void {
    const seasonSetupStates: CompanyStateEnum[] = [
      CompanyStateEnum.Onboarding,
      CompanyStateEnum.ReOnboarding,
    ];
    if (seasonSetupStates.includes(company.state)) {
      this.navService.showSeasonSetupNavigation();
      this._redirectToSeasonSetup();
    } else {
      this.navService.hideSeasonSetupNavigation();
      this.hasRedirectedToSeasonSetup = false;
      // remove the redirect from local storage
      // this is to accomodate future cases where the
      // company is not in the onboarding state
      localStorage.removeItem(this.SEASON_SETUP_REDIRECT_KEY);
    }
  }
}
